import React, { useEffect, useState } from 'react';
import type { DashboardAnnualDaySlot } from '../../utils/dashboardAnnualDaySlots';
import type { TodoNoteCategoryDraft } from '../../types/todoNote';
import type { TodoNoteItemView } from '../../types/todoNote';
import {
  collectCalendarTimetableInitials,
  collectFixedEventDisplayNames,
  firstFixedProgramColorHex,
  timetableInitialTextColor,
  type DashboardWeeklyCalendarLayoutMode,
} from '../../utils/dashboardWeeklyCalendar';
import type { DashboardMonthCell } from '../../utils/dashboardYearMonths';
import { DASHBOARD_CAL_DOW_EN } from '../../utils/dashboardYearMonths';
import { formatDashboardWeekMmDd } from '../../utils/dashboardSemesterWeek';
import { weeklyBehaviorRecordKey } from './DashboardWeeklyPlanner';
import DashboardCalendarFixedEventMarquee from './DashboardCalendarFixedEventMarquee';
import DashboardWeeklyLessonActionMenu from './DashboardWeeklyLessonActionMenu';

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

type Props = {
  cells: readonly DashboardMonthCell[];
  month: Date;
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso: Readonly<Record<string, string>>;
  slotsByDateIso: Readonly<Record<string, DashboardAnnualDaySlot[]>>;
  todosByDateIso: Readonly<Record<string, { item: TodoNoteItemView; category: TodoNoteCategoryDraft | null }[]>>;
  layoutMode: DashboardWeeklyCalendarLayoutMode;
  isMobileLayout: boolean;
  onAddTodo?: (dateIso: string) => void;
  onOpenTodo: (itemId: string) => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  onOpenProgram?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
  /** 고정(행사) 프로그램 시간표만 표시 */
  fixedEventsOnly?: boolean;
  /** 요일 행 위 섹션 헤드 */
  sectionHead?: React.ReactNode;
};

const DashboardWeeklyCalendar: React.FC<Props> = ({
  cells,
  month,
  holidayIsoSet,
  holidayNoteByIso,
  slotsByDateIso,
  todosByDateIso,
  layoutMode,
  isMobileLayout,
  onAddTodo,
  onOpenTodo,
  onOpenPresentation,
  onOpenProgram,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
  fixedEventsOnly = false,
  sectionHead,
}) => {
  const monthIndex = month.getMonth();
  const todayIso = (() => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const d = String(n.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();
  const chipInteractive = Boolean(onOpenPresentation || onOpenLessonRecord || onOpenBehaviorRecord);
  const [menu, setMenu] = useState<{ slot: DashboardAnnualDaySlot; rect: DOMRect } | null>(null);
  const menuSlotKey = menu
    ? weeklyBehaviorRecordKey(menu.slot.dateIso, menu.slot.slotIndex)
    : null;

  const onToggleChipMenu = (slot: DashboardAnnualDaySlot, el: HTMLElement) => {
    const key = weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
    setMenu((prev) => {
      if (prev && weeklyBehaviorRecordKey(prev.slot.dateIso, prev.slot.slotIndex) === key) {
        return null;
      }
      return { slot, rect: el.getBoundingClientRect() };
    });
  };

  useEffect(() => {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = (slotsByDateIso[prev.slot.dateIso] ?? []).find((s) => s.slotIndex === prev.slot.slotIndex);
      if (!next) return prev;
      if (
        next.display.progressUnit === prev.slot.display.progressUnit &&
        next.display.progressLearning === prev.slot.display.progressLearning &&
        next.display.progressLearningNote === prev.slot.display.progressLearningNote &&
        next.templateLabel === prev.slot.templateLabel
      ) {
        return prev;
      }
      return { ...prev, slot: next };
    });
  }, [slotsByDateIso]);

  return (
    <div
      className={
        'cp-dash-weekly-cal-wrap' +
        (isMobileLayout && layoutMode === 'fit' ? ' is-fit' : ' is-expand')
      }
      aria-label={`${month.getFullYear()}년 ${monthIndex + 1}월 캘린더`}
    >
      {sectionHead}
      <div className="cp-dash-weekly-cal-body">
        <div className="cp-dash-weekly-cal-grid cp-dash-weekly-cal-grid--weekday" aria-hidden>
          {WEEKDAY_KO.map((w, i) => (
            <div key={DASHBOARD_CAL_DOW_EN[i]} className="cp-dash-weekly-cal-weekday">
              {w}
            </div>
          ))}
        </div>
        <div className="cp-dash-weekly-cal-grid">
        {cells.map((cell) => {
          const iso = cell.iso ?? '';
          const inMonth = cell.day != null;
          const isSunday = cell.dow === 0;
          const isSaturday = cell.dow === 6;
          const isWeekend = isSunday || isSaturday;
          const holidayName = iso ? (holidayNoteByIso[iso] ?? '').trim() : '';
          const isHoliday = Boolean(iso && (holidayIsoSet.has(iso) || isWeekend));
          const isToday = Boolean(iso) && iso === todayIso;
          const daySlots = iso ? slotsByDateIso[iso] ?? [] : [];
          const chips = iso
            ? collectCalendarTimetableInitials(daySlots, { fixedOnly: fixedEventsOnly })
            : [];
          const fixedEventNames = iso ? collectFixedEventDisplayNames(daySlots) : [];
          const fixedFillHex = iso ? firstFixedProgramColorHex(daySlots) : null;
          const dayTodos = (iso ? todosByDateIso[iso] ?? [] : []).filter(
            (row) => (row.item.title ?? '').trim().length > 0
          );
          const useTwoCol = dayTodos.length >= 5;
          const canAdd = Boolean(iso && onAddTodo);
          return (
            <article
              key={`${iso}-${cell.dow}-${cell.day ?? 'x'}`}
              className={
                'cp-dash-weekly-cal-cell' +
                (inMonth ? ' is-current-month' : ' is-outside') +
                (isHoliday ? ' is-holiday' : '') +
                (isToday ? ' is-today' : '') +
                (fixedFillHex ? ' is-fixed-event-fill' : '')
              }
              style={
                fixedFillHex
                  ? ({ ['--cp-dash-cal-fixed-fill' as string]: fixedFillHex } as React.CSSProperties)
                  : undefined
              }
            >
              <header className="cp-dash-weekly-cal-day-head">
                <span
                  className={
                    'cp-dash-weekly-day-date' +
                    (isToday ? ' is-today' : '') +
                    (isSaturday ? ' is-sat' : '')
                  }
                  aria-current={isToday ? 'date' : undefined}
                >
                  {inMonth ? cell.day : iso ? Number(iso.slice(8, 10)) : ''}
                </span>
                {fixedEventNames.length > 0 ? (
                  <DashboardCalendarFixedEventMarquee names={fixedEventNames} />
                ) : holidayName ? (
                  <span className="cp-dash-weekly-day-holiday-name">{holidayName}</span>
                ) : null}
              </header>
              {chips.length > 0 ? (
                <div className="cp-dash-weekly-cal-periods" aria-label="수업 시간표">
                  {chips.map((chip, idx) => {
                    const period = (chip.source.periodLabel ?? '').trim();
                    const label = period ? `${period} ${chip.ch}` : chip.ch;
                    const chipKey = weeklyBehaviorRecordKey(chip.source.dateIso, chip.source.slotIndex);
                    const className =
                      'cp-dash-weekly-cal-period' +
                      (chipInteractive ? ' is-interactive' : '') +
                      (menuSlotKey === chipKey ? ' is-open' : '');
                    const style = {
                      backgroundColor: chip.colorHex || '#c0c4cc',
                      color: timetableInitialTextColor(chip.colorHex),
                    };
                    if (!chipInteractive) {
                      return (
                        <span
                          key={`${iso}-p-${idx}-${chip.source.slotIndex}-${chip.ch}`}
                          className={className}
                          title={label}
                          style={style}
                        >
                          {chip.ch}
                        </span>
                      );
                    }
                    return (
                      <button
                        key={`${iso}-p-${idx}-${chip.source.slotIndex}-${chip.ch}`}
                        type="button"
                        className={className}
                        title={label}
                        aria-label={`${label} 수업 메뉴`}
                        aria-expanded={menuSlotKey === chipKey}
                        style={style}
                        onClick={(e) => onToggleChipMenu(chip.source, e.currentTarget)}
                      >
                        {chip.ch}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="cp-dash-weekly-cal-periods is-empty" aria-hidden />
              )}
              <div
                className={
                  'cp-dash-weekly-cal-items' + (useTwoCol ? ' is-two-col' : '')
                }
              >
                {dayTodos.map(({ item, category }) => {
                  const catColor = category?.color?.trim() || '#c0c4cc';
                  const title = (item.title ?? '').trim();
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={
                        'cp-dash-weekly-cal-item' +
                        (item.completed ? ' is-completed' : '') +
                        (item.is_routine || item.routineParentId ? ' is-routine' : '')
                      }
                      style={{ ['--todo-cal-cat-color' as string]: catColor }}
                      onClick={() => onOpenTodo(item.id)}
                      aria-label={`TODO 수정: ${title}`}
                    >
                      <span
                        className="cp-dash-weekly-cal-item-dot"
                        style={{ background: catColor }}
                      />
                      <span className="cp-dash-weekly-cal-item-title">{title}</span>
                    </button>
                  );
                })}
                {canAdd ? (
                  <button
                    type="button"
                    className="cp-dash-weekly-todo-add"
                    onClick={() => onAddTodo?.(iso)}
                    aria-label={`${formatDashboardWeekMmDd(iso)} TODO 추가`}
                    title="TODO 추가"
                  >
                    +
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
        </div>
      </div>
      {menu ? (
        <DashboardWeeklyLessonActionMenu
          slot={menu.slot}
          anchorRect={menu.rect}
          onClose={() => setMenu(null)}
          onOpenPresentation={onOpenPresentation}
          onOpenProgram={onOpenProgram}
          onOpenLessonRecord={onOpenLessonRecord}
          onOpenBehaviorRecord={onOpenBehaviorRecord}
        />
      ) : null}
    </div>
  );
};

export default DashboardWeeklyCalendar;
